import React, { useState, useEffect } from "react";
import ICategory from '../menu/categories/ICategory';
import { useTranslation } from 'react-i18next';
import EColourPanel from "../../interfaces/EColourPanel";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import { useSelector } from "react-redux";
import { FormPermissions } from "../../Permissions";
import { IReduxAppState } from "../../reducers";
import useTask from "../../hooks/useTask";
import { subcategoryItemAPICall } from "../menu/products/CategoriesContainer";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import ListLoading from "../util/Loading";

import { createCategoryFormRows } from "../../formSchemas/category.form";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { IDialogRouteChildren } from "../dialogRoutes/DialogRoute";
import { useChangedDialog } from "../../hooks/useChangedDialog";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";

export interface ICategoriesDialogForm extends IDialogRouteChildren {
    formData: any;
    categories: ICategory[];
    isNewItem: boolean;
    callSubcategory: subcategoryItemAPICall;
    setCategoryID: (id: string) => void;
    venueId: string;
    id: string;
}

export function CategoriesDialogForm(props: ICategoriesDialogForm): JSX.Element {
    const {
        formData,
        categories,
        callSubcategory,
        isNewItem,
        onClose,
        setCategoryID,
        venueId,
        id,
        onDialogClose
    } = props;

    const { t } = useTranslation();
    const [isSquare, setIsSquare] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { hasFormChanged } = useChangedDialog();

    const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.MENU_MANAGEMENT);

    const venue = useSelector((state: IReduxAppState) => state.venue[venueId]?.venueData);
    const isEposNow = useSelector((state: IReduxAppState) => state.isEposNow);
    const subscription = useSelector((state: IReduxAppState) => state.subscription);
    const { isLoading: isCatLoading, apiData } = useAPIV2<ICategory[]>(APIExtensions.menuItemCategories, { yoelloAPIParams: { venue_id: venueId }, onMountDisabled: categories ? true : false });
    const { isLoading: isSubLoading, apiData: subCats } = useAPIV2(APIExtensions.menuItemSubcategories,
        { yoelloAPIParams: { venue_id: venueId }, query: id, onMountDisabled: isNewItem ? true : false })

    const { increaseDoneTasks } = useTask();

    useEffect(() => {
        if (venue?.payment_processor === 'SQUARE') {
            setIsSquare(true);
        } else {
            setIsSquare(false);
        }
    }, [venue]);

    async function onHandleSubmit(data: any) {
        setIsLoading(true);
        setCategoryID(data.category_id);
        const body = {
            ...data,
            venue_id: venueId
        };
        if (isNewItem) {
            await callSubcategory(HTTPMethods.POST, { body });
        } else {
            await callSubcategory(HTTPMethods.PUT, { body, query: id });
        }
        setIsLoading(false);
        if (onDialogClose) {
            onDialogClose();
        } else {
            onClose();
        }
        increaseDoneTasks();
    }

    const formState = isNewItem ? { enabled: true } : formData ?? subCats;

    return isLoading || (isCatLoading && !categories) || ((!isNewItem && isSubLoading) && !formData) ?
        <ListLoading /> :
        <div className="dialog">
            <DynamicForm
                formData={formState}
                formKeyName="mysettings"
                formRows={createCategoryFormRows(props, categories || apiData, isEposNow, isNewItem, t, isSquare , onFailedInteraction)}
                onHandleSubmit={onHandleSubmit}
                disabledFields={FormPermissions["subcategorydialog"]}
                subscriptionKey={subscription}
                isDialog={true}
                hasDataChanged={hasFormChanged}
                disableAll={!hasPermission}
                disableAllInteraction={onFailedInteraction}
                formButtons={{
                    leftButton: {
                        type: "button", label: t('forms.buttons.cancel'), color: EColourPanel.DEFAULT, fullWidth: false, onClick: onClose
                    },
                    rightButton: {
                        type: "submit", label: t('forms.buttons.submitupdates'), color: EColourPanel.PRIMARY, fullWidth: false, disabled: isLoading || !hasPermission
                    }
                }}
            />
        </div>;
}

